import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { dataService } from '../../services/DataService'

export const CustomerOrProspectLookup = ({
  label,
  value,
  onChange,
  onTypeChange, // Callback to notify parent if it's a customer or prospect
  onResolvingChange, // Callback: true while an external value is being validated against Dataverse
  onSelectionChange,
  error,
  placeholder,
  className,
  required,
  ...props
}) => {
  const { t } = useTranslation('lookup')
  const [results, setResults] = useState([])
  const [filteredResults, setFilteredResults] = useState([])
  const [searching, setSearching] = useState(false)
  const [loadError, setLoadError] = useState(null)
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedItem, setSelectedItem] = useState(null)
  const [hasSearched, setHasSearched] = useState(false)
  // How the current value was resolved: 'customer' | 'prospect' | 'new-prospect' | 'ambiguous' | 'unresolved' | null
  const [resolution, setResolution] = useState(null)
  const [resolving, setResolving] = useState(false)
  const dropdownRef = useRef(null)
  const inputRef = useRef(null)
  const searchTimeoutRef = useRef(null)
  // Last value we resolved (auto or manual) so the auto-resolve effect doesn't loop on it.
  const resolvedValueRef = useRef(null)

  const resolvedLabel = label || t('customerOrProspect.label')
  const resolvedPlaceholder = placeholder || t('customerOrProspect.placeholder')

  const buildNewProspectItem = (term) => ({
    id: term,
    name: term,
    code: term,
    type: 'prospect',
    typeLabel: 'Prospect',
    isNew: true
  })

  const resolutionForItem = (item) => {
    if (!item) return null
    if (item.type === 'customer') return 'customer'
    if (item.type === 'prospect') return item.isNew ? 'new-prospect' : 'prospect'
    return null
  }

  // Search both customers AND prospects for a term. The API client handles caching.
  const fetchResults = useCallback(async (term) => {
    console.log(`Searching customers and prospects for: "${term}"`)

    // Backend already filters by user's company via JWT token
    const [customersResult, prospectsResult] = await Promise.all([
      dataService.getCustomers(null, term), // No limit - search filter handles relevance
      dataService.getProspects(term) // Filtered server-side (name or id)
    ])

    const combinedResults = []

    if (customersResult.success && customersResult.data) {
      customersResult.data.forEach(customer => {
        combinedResults.push({
          id: customer.mserp_customeraccount,
          name: customer.mserp_organizationname || customer.mserp_namealias || customer.mserp_customeraccount,
          code: customer.mserp_customeraccount,
          type: 'customer',
          typeLabel: 'Customer',
          data: customer
        })
      })
    }

    if (prospectsResult.success && prospectsResult.data) {
      const searchLower = term.toLowerCase()
      prospectsResult.data.forEach(prospect => {
        const name = (prospect.mserp_prospectname || '').toLowerCase()
        const id = (prospect.mserp_prospectid || '').toLowerCase()

        if (name.includes(searchLower) || id.includes(searchLower)) {
          combinedResults.push({
            id: prospect.mserp_prospectid,
            name: prospect.mserp_prospectname || prospect.mserp_prospectid,
            code: prospect.mserp_prospectid,
            type: 'prospect',
            typeLabel: 'Prospect',
            data: prospect
          })
        }
      })
    }

    // Remove duplicates and sort (customers first, then prospects)
    combinedResults.sort((a, b) => {
      if (a.type !== b.type) {
        return a.type === 'customer' ? -1 : 1
      }
      return a.name.localeCompare(b.name)
    })

    const uniqueResults = []
    const seenIds = new Set()
    combinedResults.forEach(item => {
      const key = `${item.type}-${item.id}`
      if (!seenIds.has(key)) {
        seenIds.add(key)
        uniqueResults.push(item)
      }
    })

    console.log(`Found ${uniqueResults.length} results (${uniqueResults.filter(r => r.type === 'customer').length} customers, ${uniqueResults.filter(r => r.type === 'prospect').length} prospects)`)

    return uniqueResults
  }, [])

  // Debounced search function used while the user types
  const debouncedSearch = useCallback(async (term) => {
    if (!term || term.length < 3) {
      setFilteredResults([])
      setSearching(false)
      return
    }

    setSearching(true)
    setLoadError(null)
    setHasSearched(true)

    try {
      const uniqueResults = await fetchResults(term)
      setResults(uniqueResults)
      setFilteredResults(uniqueResults)
    } catch (error) {
      console.error('Error searching:', error)
      setLoadError(t('customerOrProspect.searchFailed'))
      setFilteredResults([])
    } finally {
      setSearching(false)
    }
  }, [t, fetchResults])

  // Pick the single item a free-text value maps to, or null when it is ambiguous.
  // Priority: exact code match -> exact name match -> only result.
  const pickUniqueMatch = (term, items) => {
    const lower = term.trim().toLowerCase()
    const exactCode = items.filter(r => (r.code || '').toLowerCase() === lower)
    if (exactCode.length === 1) return exactCode[0]
    if (exactCode.length > 1) return null

    const exactName = items.filter(r => (r.name || '').toLowerCase() === lower)
    if (exactName.length === 1) return exactName[0]
    if (exactName.length > 1) return null

    return items.length === 1 ? items[0] : null
  }

  const applySelection = (item) => {
    resolvedValueRef.current = item.id
    setSelectedItem(item)
    setResolution(resolutionForItem(item))
    setSearchTerm('')
    setIsOpen(false)
    if (onChange) onChange(item.id)
    if (onTypeChange) onTypeChange(item.type)
    if (onSelectionChange) onSelectionChange(item)
  }

  // Resolve a value that arrived from outside (URL prefill, parent state) instead of a click in the list.
  const resolveExternalValue = useCallback(async (term) => {
    resolvedValueRef.current = term

    if (term.trim().length < 3) {
      // Search needs 3 chars - the user has to pick manually.
      setResolution('unresolved')
      setSearchTerm(term)
      setIsOpen(true)
      if (onTypeChange) onTypeChange(null)
      return
    }

    setSearching(true)
    setResolving(true)
    if (onResolvingChange) onResolvingChange(true)
    setLoadError(null)
    setHasSearched(true)

    try {
      const items = await fetchResults(term)
      setResults(items)
      setFilteredResults(items)

      if (items.length === 0) {
        console.log(`No customer/prospect found for "${term}" - using it as a new prospect`)
        applySelection(buildNewProspectItem(term))
        return
      }

      const match = pickUniqueMatch(term, items)
      if (match) {
        console.log(`Auto-resolved "${term}" to ${match.type} ${match.id}`)
        applySelection(match)
        return
      }

      // Several candidates and none of them is an exact match - the user has to choose.
      console.log(`"${term}" is ambiguous (${items.length} matches) - user must choose`)
      setResolution('ambiguous')
      setSelectedItem({ id: term, name: term, code: term, type: 'unknown' })
      setSearchTerm(term)
      setIsOpen(true)
      if (onTypeChange) onTypeChange(null)
    } catch (error) {
      console.error('Error resolving value:', error)
      setLoadError(t('customerOrProspect.searchFailed'))
      setResolution('unresolved')
      setSearchTerm(term)
      setIsOpen(true)
      if (onTypeChange) onTypeChange(null)
    } finally {
      setSearching(false)
      setResolving(false)
      if (onResolvingChange) onResolvingChange(false)
    }
  }, [t, fetchResults, onChange, onTypeChange, onResolvingChange]) // eslint-disable-line react-hooks/exhaustive-deps

  // Keep selectedItem in sync with the value coming from the parent
  useEffect(() => {
    if (!value) {
      setSelectedItem(null)
      setResolution(null)
      resolvedValueRef.current = null
      return
    }

    // Already selected (click or auto-resolve) - don't clobber it with an 'unknown' item.
    if (selectedItem && selectedItem.id === value && selectedItem.type !== 'unknown') {
      return
    }

    const item = results.find(r => r.id === value)
    if (item) {
      setSelectedItem(item)
      if (onSelectionChange) onSelectionChange(item)
      return
    }

    setSelectedItem({ id: value, name: value, code: value, type: 'unknown' })

    // New value we haven't validated yet (e.g. prefilled from URL) - resolve it against Dataverse.
    if (resolvedValueRef.current !== value) {
      resolveExternalValue(value)
    }
  }, [value, results]) // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced search effect
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }

    if (searchTerm && searchTerm.length >= 3) {
      searchTimeoutRef.current = setTimeout(() => {
        debouncedSearch(searchTerm)
      }, 300)
    } else if (searchTerm && searchTerm.length < 3) {
      setFilteredResults([])
      setSearching(false)
    } else {
      setFilteredResults([])
      setSearching(false)
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [searchTerm, debouncedSearch])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleInputClick = () => {
    setIsOpen(true)
  }

  const handleInputChange = (e) => {
    const newValue = e.target.value
    setSearchTerm(newValue)
    setIsOpen(true)

    setSelectedItem(null)
    setResolution(null)

    if (!newValue) {
      setFilteredResults([])
      setHasSearched(false)
      resolvedValueRef.current = null
      if (onChange) {
        onChange('')
      }
      if (onTypeChange) {
        onTypeChange(null)
      }
      return
    }

    // Don't call onChange during typing - only when selecting
  }

  const handleItemSelect = (item) => {
    console.log(`Selected ${item.type}:`, item.id)
    applySelection(item) // Notifies parent: 'customer' or 'prospect'
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setIsOpen(false)
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      setIsOpen(true)
    }
  }

  const displayValue = selectedItem
    ? selectedItem.name === selectedItem.code
      ? selectedItem.code
      : `${selectedItem.name} (${selectedItem.code})`
    : value || ''

  return (
    <div>
      {resolvedLabel && (
        <label className="block text-sm font-medium text-neutral-700 mb-2">
          {resolvedLabel}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      <div className="relative" ref={dropdownRef}>
        <input
          ref={inputRef}
          type="text"
          value={isOpen ? searchTerm : displayValue}
          onChange={handleInputChange}
          onClick={handleInputClick}
          onKeyDown={handleKeyDown}
          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
            error ? 'border-red-500' : 'border-neutral-300'
          } ${searching ? 'bg-neutral-100' : 'bg-white'} ${className || ''}`}
          placeholder={resolvedPlaceholder}
          {...props}
        />

        {searching && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
          </div>
        )}

        {isOpen && (
          <div className="absolute z-[9999] w-full mt-1 bg-white border border-neutral-300 rounded-lg shadow-lg max-h-80 overflow-y-auto">
            {loadError && (
              <div className="p-3 text-center text-red-600">
                {t('customerOrProspect.errorDisplay', { error: loadError })}
              </div>
            )}

            {!loadError && !searching && searchTerm && searchTerm.length < 3 && (
              <div className="p-3 text-center text-neutral-600">
                {t('customerOrProspect.typeMinChars')}
              </div>
            )}

            {!loadError && !searching && searchTerm && searchTerm.length >= 3 && filteredResults.length === 0 && hasSearched && (
              <div className="p-3">
                <div className="text-center text-neutral-600 mb-2">
                  {t('customerOrProspect.noResults', { searchTerm })}
                </div>
                <div
                  onClick={() => handleItemSelect(buildNewProspectItem(searchTerm))}
                  className="px-3 py-2 cursor-pointer hover:bg-green-50 border border-green-200 rounded-md bg-green-25 transition-colors"
                >
                  <div className="font-medium text-gray-800">
                    {t('customerOrProspect.useAsProspect', { searchTerm })}
                  </div>
                  <div className="text-sm text-gray-600">
                    {t('customerOrProspect.useAsProspectHint')}
                  </div>
                </div>
              </div>
            )}

            {!loadError && !searchTerm && !hasSearched && (
              <div className="p-3 text-center text-neutral-600">
                {t('customerOrProspect.typeToSearch')}
              </div>
            )}

            {searching && (
              <div className="p-3 text-center text-neutral-600">
                <div className="flex items-center justify-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
                  <span>{t('customerOrProspect.searching')}</span>
                </div>
              </div>
            )}

            {!loadError && !searching && filteredResults.length > 0 && (
              <div className="py-1">
                {/* Group by type */}
                {filteredResults.filter(r => r.type === 'customer').length > 0 && (
                  <>
                    <div className="px-3 py-2 text-xs font-semibold text-neutral-500 bg-neutral-50 border-b">
                      {t('customerOrProspect.customersHeader', { count: filteredResults.filter(r => r.type === 'customer').length })}
                    </div>
                    {filteredResults.filter(r => r.type === 'customer').map((item, index) => (
                      <div
                        key={`customer-${item.id}-${index}`}
                        onClick={() => handleItemSelect(item)}
                        className="px-3 py-2 cursor-pointer hover:bg-blue-50 transition-colors border-l-2 border-transparent hover:border-blue-500"
                      >
                        <div className="font-medium text-neutral-800">
                          {item.name}
                        </div>
                        <div className="text-sm text-neutral-600">
                          {item.code}
                        </div>
                      </div>
                    ))}
                  </>
                )}

                {filteredResults.filter(r => r.type === 'prospect').length > 0 && (
                  <>
                    <div className="px-3 py-2 text-xs font-semibold text-neutral-500 bg-neutral-50 border-b border-t">
                      {t('customerOrProspect.prospectsHeader', { count: filteredResults.filter(r => r.type === 'prospect').length })}
                    </div>
                    {filteredResults.filter(r => r.type === 'prospect').map((item, index) => (
                      <div
                        key={`prospect-${item.id}-${index}`}
                        onClick={() => handleItemSelect(item)}
                        className="px-3 py-2 cursor-pointer hover:bg-green-50 transition-colors border-l-2 border-transparent hover:border-green-500"
                      >
                        <div className="font-medium text-neutral-800">
                          {item.name}
                        </div>
                        <div className="text-sm text-neutral-600">
                          {item.code}
                        </div>
                      </div>
                    ))}
                  </>
                )}

              </div>
            )}
          </div>
        )}
      </div>

      {resolving && (
        <p className="mt-1 text-sm flex items-start gap-1.5 text-neutral-600">
          <span className="mt-0.5 flex-shrink-0 animate-spin rounded-full h-3 w-3 border-b-2 border-primary-600"></span>
          <span>{t('customerOrProspect.resolution.validating', { value: value || '' })}</span>
        </p>
      )}

      {resolution && !resolving && !error && (
        <p className={`mt-1 text-sm flex items-start gap-1.5 ${resolutionStyles[resolution] || 'text-neutral-600'}`}>
          <span className={`inline-block w-2 h-2 mt-1.5 flex-shrink-0 rounded-full ${resolutionDotStyles[resolution] || 'bg-neutral-400'}`}></span>
          <span>{t(`customerOrProspect.resolution.${resolution}`, { value: selectedItem?.code || value || '' })}</span>
        </p>
      )}

      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}
    </div>
  )
}

// Visual style of the resolution hint shown under the input
const resolutionStyles = {
  customer: 'text-blue-700',
  prospect: 'text-green-700',
  'new-prospect': 'text-green-700',
  ambiguous: 'text-amber-700',
  unresolved: 'text-amber-700'
}

const resolutionDotStyles = {
  customer: 'bg-blue-500',
  prospect: 'bg-green-500',
  'new-prospect': 'bg-green-500',
  ambiguous: 'bg-amber-500',
  unresolved: 'bg-amber-500'
}
